import React, { useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import { Form, Select } from 'formik-antd';
import { Button, Modal, Row, Col } from 'antd';
import pluralize from 'pluralize';
import * as Yup from 'yup';

import {
  getAccountNumbers,
  getACHPaymentFile,
  addAccountNumberToPayment,
} from 'redux/reducers/achPaymentsSlice';
import { downloadFile } from 'utils/fileUtils';
import FormItem from 'components/formik/FormItem';
import BaseAlert from 'components/wrappers/BaseAlert';
import SubmitButton from 'components/formik/SubmitButton';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { ResourceDownloadResponse } from 'api/adminApiClient';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { useAppDispatch } from 'hooks/useAppDispatch';

type Props = {
  onClose: () => void;
  selectedPaymentIds: string[];
  onAccNumberAdded: () => Promise<void>;
};

interface AccountNumberFormValues {
  accountNumber: string;
}

const validationSchema = Yup.object({
  accountNumber: Yup.string().nullable().trim().required('Field is required'),
});

export default function AccountNumberModal(props: Props) {
  const { onClose, selectedPaymentIds, onAccNumberAdded } = props;
  const [paymentIds] = useState(selectedPaymentIds);
  const [accountNumbers, setAccountNumbers] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  const formRef: any = useRef();
  const paymentsCount = paymentIds.length;
  const initialValues: AccountNumberFormValues = {
    accountNumber: '',
  };

  useEffect(() => {
    loadAccountNumbers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAccountNumbers = async () => {
    const result = await dispatch(getAccountNumbers());

    if (getAccountNumbers.fulfilled.match(result)) {
      setAccountNumbers(result.payload);
    }
  };

  const getPluralizedName = (name: string, inclusive = false) => {
    return pluralize(name, paymentsCount, inclusive);
  };

  const handleModalCancel = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  const loadACHPaymentFile = async (fileKey: string) => {
    const result = await dispatch(getACHPaymentFile(fileKey));

    if (getACHPaymentFile.fulfilled.match(result)) {
      return result.payload;
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);

      return Promise.reject();
    }
  };

  const downloadAccountNumberFile = async (
    downloadResponse: ResourceDownloadResponse,
  ) => {
    try {
      const { downloadUrl, fileName } = downloadResponse;

      await downloadFile({ downloadUrl, fileName });
    } catch (e: any) {
      BaseAlert.error(e.message);
    }
  };

  const downloadACHPaymentFiles = async () => {
    const responses = await Promise.allSettled(
      paymentIds.map(loadACHPaymentFile),
    );

    const files: ResourceDownloadResponse[] = responses
      .filter((response) => response.status === 'fulfilled')
      .map((response) => response.value);

    if (!files.length) return;

    await Promise.allSettled(files.map(downloadAccountNumberFile));

    BaseAlert.success(
      `${getPluralizedName('file', true)} ${getPluralizedName(
        'was',
      )} successfully updated, downloaded, and removed from the list.`,
    );
  };

  const handleSubmit = async (values: AccountNumberFormValues) => {
    const { accountNumber } = values;

    const result = await dispatch(
      addAccountNumberToPayment({
        accountNumber,
        paymentIds,
      }),
    );

    if (addAccountNumberToPayment.fulfilled.match(result)) {
      await Promise.all([onAccNumberAdded(), downloadACHPaymentFiles()]);
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <BaseModal
      title="Add account number"
      maxWidth={468}
      open={true}
      onCancel={handleModalCancel}
    >
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            <div className="modal-text">
              You have selected {getPluralizedName('ACH payment', true)} to
              which you want to add the account number. Please select one below.
              After the update {getPluralizedName('file')} will be exported to
              your PC and removed from the list in Back-Office.
            </div>

            <Row gutter={20}>
              <Col xs={24}>
                <FormItem
                  name="accountNumber"
                  label="Account number *"
                  data-id="accountNumberField"
                >
                  <Select
                    name="accountNumber"
                    placeholder="Select account number"
                  >
                    {accountNumbers.map((accountNumber) => (
                      <Select.Option
                        key={`select-account-number-${accountNumber}`}
                        value={accountNumber || ''}
                      >
                        {accountNumber}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
            </Row>
          </div>

          <BaseModalFooter>
            <Button onClick={handleModalCancel}>Cancel</Button>

            <SubmitButton data-id="updateFilesBtn">Update files</SubmitButton>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
